/**
 * 「今日のこと」のカード（ADR-0026 の 11。本人の決定: 最初は PWA のカード。いずれ磨く）。
 * 会話の上、「昨夜の整理」のカードの上に出す。何も無ければ出さない。押したら描き直す。
 * 声の端末・未適用の環境・無効の間は出ない（Gateway が空・403・500 を返す）。
 */
import { ApiError, api } from "./api.js";
import {
  type BriefingItemView,
  type BriefingView,
  cardModel,
  itemButtons,
  press,
} from "./briefing-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  e.append(...children);
  return e;
}

export async function renderBriefingCard(
  root: HTMLElement,
  opts: {
    /** 読めたとき、まとめの全体を知らせる（見出しの下の「今いちばん大事なこと」に使う。ADR-0055） */
    onView?: (v: BriefingView) => void;
  } = {},
): Promise<void> {
  let view: BriefingView;
  try {
    view = await api<BriefingView>("/v1/briefing");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  opts.onView?.(view);
  const m = cardModel(view);
  if (!m.show) {
    root.replaceChildren();
    return;
  }
  const status = el("p", { class: "muted small" });
  const row = (item: BriefingItemView) => {
    const buttons = el("div", { class: "row wrap" });
    for (const b of itemButtons(item)) {
      const cls = [b.primary ? "primary" : "", b.pressed ? "pressed" : ""]
        .filter(Boolean)
        .join(" ");
      const btn = el("button", cls ? { class: cls } : {}, b.label) as HTMLButtonElement;
      if (b.pressed) btn.setAttribute("aria-pressed", "true");
      btn.onclick = async () => {
        btn.disabled = true;
        try {
          await press((p, body) => api(p, body), item, b.body);
          await renderBriefingCard(root, opts);
        } catch (e) {
          const code = e instanceof ApiError ? e.code : String(e);
          const msg: Record<string, string> = {
            conflict: "今はできません（状態が変わっています）。開き直してください。",
            not_allowed: "これには使えません。",
            frozen: "今は止まっています（凍結中）。",
          };
          status.textContent = msg[code] ?? `反映できませんでした（${code}）`;
          btn.disabled = false;
        }
      };
      buttons.append(btn);
    }
    return el(
      "div",
      { class: `briefing-item${item.status === "done" ? " done" : ""}` },
      el("p", {}, item.status === "done" ? `済み: ${item.text}` : item.text),
      buttons,
    );
  };
  const closed = m.closed.length
    ? el(
        "details",
        { class: "closed" },
        el("summary", {}, `閉じたもの（${m.closed.length}）`),
        ...m.closed.map(row),
      )
    : "";
  root.replaceChildren(
    el(
      "section",
      { class: "card briefing" },
      el("h2", {}, "今日のこと"),
      ...m.active.map(row),
      m.nightlyLine ? el("p", { class: "small muted" }, m.nightlyLine) : "",
      m.monitorLine ? el("p", { class: "small muted" }, m.monitorLine) : "",
      closed,
      status,
    ),
  );
}
